import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  Timestamp,
  query,
  orderBy,
  where,
  arrayUnion,
  arrayRemove,
  increment,
} from 'firebase/firestore';
import {
  getAuth,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  updateProfile,
} from 'firebase/auth';
import { firestore } from '../config/firebase';
import {
  StaffUser,
  StaffPayment,
  Invitation,
  Role,
  BOOTSTRAP_ADMIN_EMAIL,
} from '../types/staff';

const usersCol = () => collection(firestore, 'users');
const invitesCol = () => collection(firestore, 'invitations');

const INVITE_TTL_DAYS = 7;

const millis = (days: number) => days * 24 * 60 * 60 * 1000;

const expiresAtFor = (days: number) =>
  Timestamp.fromMillis(Date.now() + millis(days));

/**
 * Generate an unguessable invite token.
 *
 * Prefers `crypto.randomUUID` (Node 18+, all evergreen browsers); falls
 * back to `crypto.getRandomValues` for older environments.
 */
const generateInviteToken = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID().replace(/-/g, '');
  }
  const buf = new Uint8Array(16);
  crypto.getRandomValues(buf);
  return Array.from(buf, (b) => b.toString(16).padStart(2, '0')).join('');
};

/* -------------------------------------------------------------------------- */
/* Users                                                                       */
/* -------------------------------------------------------------------------- */

export const getStaffUser = async (uid: string): Promise<StaffUser | null> => {
  const snap = await getDoc(doc(usersCol(), uid));
  if (!snap.exists()) return null;
  return { id: snap.id, ...(snap.data() as Omit<StaffUser, 'id'>) };
};

export const listStaffUsers = async (): Promise<StaffUser[]> => {
  const snap = await getDocs(query(usersCol(), orderBy('createdAt', 'desc')));
  return snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<StaffUser, 'id'>) }));
};

export const updateStaffRole = async (uid: string, role: Role): Promise<void> => {
  await updateDoc(doc(usersCol(), uid), { role });
};

export const setStaffDisabled = async (uid: string, disabled: boolean): Promise<void> => {
  await updateDoc(doc(usersCol(), uid), { disabled });
};

/**
 * Record a payment made to a staff member. Uses atomic field operations
 * (arrayUnion for the ledger entry + increment for the running total) so
 * concurrent add/remove from multiple admins can't clobber each other via a
 * stale read-modify-write. Admin-only (enforced by the Firestore users update
 * rule). Returns the new entry. A non-positive or invalid amount is rejected.
 */
export const addStaffPayment = async (
  uid: string,
  amount: number,
  date: string,
  note?: string,
  recordedBy?: string,
): Promise<StaffPayment> => {
  if (!Number.isFinite(amount) || amount <= 0) {
    throw new Error('Payment amount must be greater than 0');
  }
  const entry: StaffPayment = {
    id: `staffpay_${Date.now()}`,
    amount,
    date,
  };
  if (note && note.trim()) entry.note = note.trim();
  if (recordedBy) entry.recordedBy = recordedBy;

  await updateDoc(doc(usersCol(), uid), {
    staffPayments: arrayUnion(entry),
    staffPayTotal: increment(amount),
  });
  return entry;
};

/**
 * Remove a previously-recorded staff payment by id and decrement the running
 * total by exactly that entry's amount. The entry is read first so arrayRemove
 * can match it by value; the removal + decrement themselves are atomic field
 * ops, so a concurrent add on the same doc is preserved. Admin-only. No-op if
 * the id isn't present.
 */
export const removeStaffPayment = async (
  uid: string,
  paymentId: string,
): Promise<void> => {
  const ref = doc(usersCol(), uid);
  const snap = await getDoc(ref);
  const data = (snap.data() as Record<string, unknown>) || {};
  const existing: StaffPayment[] = Array.isArray(data.staffPayments)
    ? (data.staffPayments as StaffPayment[])
    : [];
  const entry = existing.find((p) => p.id === paymentId);
  if (!entry) return;

  await updateDoc(ref, {
    staffPayments: arrayRemove(entry),
    staffPayTotal: increment(-(Number(entry.amount) || 0)),
  });
};

/**
 * Remove a user from the `users` collection. NOTE: this does NOT delete the
 * Firebase Auth account — Auth user deletion requires admin SDK / Functions.
 * The deleted user will be signed out on their next session because they
 * have no user doc, blocked by AuthContext.
 */
export const removeStaffUser = async (uid: string): Promise<void> => {
  await deleteDoc(doc(usersCol(), uid));
};

/**
 * Ensure the currently authenticated user has a corresponding `users/{uid}`
 * doc. Returns the doc.
 *
 * - If a doc already exists, returns it as-is.
 * - If no doc exists and the email matches `BOOTSTRAP_ADMIN_EMAIL`, creates
 *   an admin doc and returns it.
 * - Otherwise returns null (caller should treat as unauthorized).
 */
export const ensureStaffUserForAuth = async (
  uid: string,
  email: string | null,
  displayName?: string | null,
): Promise<StaffUser | null> => {
  const existing = await getStaffUser(uid);
  if (existing) return existing;

  if (email && email.toLowerCase() === BOOTSTRAP_ADMIN_EMAIL.toLowerCase()) {
    // Build the doc without undefined fields — Firestore JS SDK throws on undefined.
    const newDoc: Record<string, unknown> = {
      email,
      role: 'admin',
      createdAt: Timestamp.now(),
    };
    if (displayName) newDoc.displayName = displayName;
    await setDoc(doc(usersCol(), uid), newDoc);
    return { id: uid, ...(newDoc as Omit<StaffUser, 'id'>) };
  }

  return null;
};

/* -------------------------------------------------------------------------- */
/* Invitations                                                                 */
/* -------------------------------------------------------------------------- */

export const createInvitation = async (
  email: string,
  role: Role,
  createdBy: string,
  createdByEmail?: string,
): Promise<Invitation> => {
  const token = generateInviteToken();
  const data: Record<string, unknown> = {
    email: email.trim().toLowerCase(),
    role,
    status: 'pending',
    createdAt: Timestamp.now(),
    createdBy,
    expiresAt: expiresAtFor(INVITE_TTL_DAYS),
  };
  if (createdByEmail) data.createdByEmail = createdByEmail;
  await setDoc(doc(invitesCol(), token), data);
  return { id: token, ...(data as Omit<Invitation, 'id'>) };
};

export const listInvitations = async (): Promise<Invitation[]> => {
  const snap = await getDocs(query(invitesCol(), orderBy('createdAt', 'desc')));
  return snap.docs.map(
    (d) => ({ id: d.id, ...(d.data() as Omit<Invitation, 'id'>) }),
  );
};

export const listInvitationsByEmail = async (email: string): Promise<Invitation[]> => {
  const snap = await getDocs(
    query(invitesCol(), where('email', '==', email.trim().toLowerCase())),
  );
  return snap.docs.map(
    (d) => ({ id: d.id, ...(d.data() as Omit<Invitation, 'id'>) }),
  );
};

export const getInvitation = async (token: string): Promise<Invitation | null> => {
  const snap = await getDoc(doc(invitesCol(), token));
  if (!snap.exists()) return null;
  return { id: snap.id, ...(snap.data() as Omit<Invitation, 'id'>) };
};

export const revokeInvitation = async (token: string): Promise<void> => {
  await updateDoc(doc(invitesCol(), token), { status: 'revoked' });
};

export const deleteInvitation = async (token: string): Promise<void> => {
  await deleteDoc(doc(invitesCol(), token));
};

/**
 * Accept an invitation: create the Firebase Auth account, create the
 * corresponding `users/{uid}` doc with the role from the invitation, and
 * mark the invitation as accepted.
 *
 * Throws if the invitation is missing, revoked, expired, already accepted,
 * or if the Auth signup fails (e.g. weak password, existing account).
 */
export const acceptInvitation = async (
  token: string,
  password: string,
  displayName?: string,
): Promise<{ uid: string; role: Role }> => {
  const invite = await getInvitation(token);
  if (!invite) throw new Error('Invitation not found');
  if (invite.status === 'accepted') throw new Error('Invitation has already been accepted');
  if (invite.status === 'revoked') throw new Error('Invitation has been revoked');
  if (invite.expiresAt.toMillis() < Date.now()) {
    throw new Error('Invitation has expired');
  }

  const auth = getAuth();

  // Try to create the Auth account. If one already exists for this email
  // (e.g. left over from a previous failed acceptance attempt before the
  // AuthContext race was fixed), fall back to signing in with the same
  // password so the rest of the acceptance can complete.
  let cred;
  try {
    cred = await createUserWithEmailAndPassword(auth, invite.email, password);
  } catch (err: any) {
    if (err?.code === 'auth/email-already-in-use') {
      try {
        cred = await signInWithEmailAndPassword(auth, invite.email, password);
      } catch (signInErr: any) {
        if (signInErr?.code === 'auth/wrong-password' || signInErr?.code === 'auth/invalid-credential') {
          throw new Error(
            'An account already exists for this email but the password does not match. Ask an admin to delete the old account, then try again.',
          );
        }
        throw signInErr;
      }
    } else {
      throw err;
    }
  }

  if (displayName) {
    try {
      await updateProfile(cred.user, { displayName });
    } catch (profileErr) {
      // Non-fatal — proceed without the display name update.
      console.warn('updateProfile failed:', profileErr);
    }
  }

  const userDoc: Record<string, unknown> = {
    email: invite.email,
    role: invite.role,
    createdAt: Timestamp.now(),
    invitedBy: invite.createdBy,
    acceptedInviteId: invite.id,
  };
  if (displayName) userDoc.displayName = displayName;

  try {
    await setDoc(doc(usersCol(), cred.user.uid), userDoc);
  } catch (err: any) {
    console.error('acceptInvitation: setDoc users failed', {
      code: err?.code,
      message: err?.message,
      uid: cred.user.uid,
      email: invite.email,
      tokenEmail: cred.user.email,
      role: invite.role,
      acceptedInviteId: invite.id,
    });
    throw err;
  }

  try {
    await updateDoc(doc(invitesCol(), invite.id), {
      status: 'accepted',
      acceptedAt: Timestamp.now(),
      acceptedBy: cred.user.uid,
    });
  } catch (err: any) {
    // The user doc was written so they can sign in — the invite update
    // failing isn't catastrophic, but log it so an admin can re-revoke
    // or delete the stale pending row.
    console.warn('acceptInvitation: invite status update failed', {
      code: err?.code,
      message: err?.message,
      inviteId: invite.id,
    });
  }

  return { uid: cred.user.uid, role: invite.role };
};

/* -------------------------------------------------------------------------- */
/* Ownership backfill                                                          */
/* -------------------------------------------------------------------------- */

/**
 * Count `clients` and `cases` docs that are missing a `createdBy` field.
 * Used by the admin Staff page to show how many docs need migration before
 * running the backfill.
 *
 * NOTE: This reads every client and case doc. It assumes the caller is an
 * admin (the only role whose Firestore reads aren't owner-restricted). A
 * staff user calling this would have most reads denied and the counts would
 * be misleading.
 */
export const countUnownedDocs = async (): Promise<{
  clients: number;
  cases: number;
}> => {
  const clientsSnap = await getDocs(collection(firestore, 'clients'));
  const casesSnap = await getDocs(collection(firestore, 'cases'));
  let clientsMissing = 0;
  let casesMissing = 0;
  clientsSnap.forEach((d) => {
    const data = d.data() as Record<string, unknown>;
    if (!data.createdBy) clientsMissing += 1;
  });
  casesSnap.forEach((d) => {
    const data = d.data() as Record<string, unknown>;
    if (!data.createdBy) casesMissing += 1;
  });
  return { clients: clientsMissing, cases: casesMissing };
};

/**
 * Assign `createdBy = ownerUid` on every client and case doc that doesn't
 * already have one. Runs in chunks to stay under Firestore's per-write limits.
 *
 * Returns the number of docs touched. Idempotent — safe to re-run.
 */
export const backfillOwnership = async (
  ownerUid: string,
): Promise<{ clients: number; cases: number }> => {
  const clientsSnap = await getDocs(collection(firestore, 'clients'));
  const casesSnap = await getDocs(collection(firestore, 'cases'));

  let clientsUpdated = 0;
  let casesUpdated = 0;

  for (const d of clientsSnap.docs) {
    const data = d.data() as Record<string, unknown>;
    if (!data.createdBy) {
      await updateDoc(doc(collection(firestore, 'clients'), d.id), {
        createdBy: ownerUid,
      });
      clientsUpdated += 1;
    }
  }

  for (const d of casesSnap.docs) {
    const data = d.data() as Record<string, unknown>;
    if (!data.createdBy) {
      await updateDoc(doc(collection(firestore, 'cases'), d.id), {
        createdBy: ownerUid,
      });
      casesUpdated += 1;
    }
  }

  return { clients: clientsUpdated, cases: casesUpdated };
};

/**
 * Recovery path used by AuthContext: if a user has just signed up via an
 * invitation but their `users/{uid}` doc never got written (e.g. the original
 * setDoc inside `acceptInvitation` lost the race against AuthContext's first
 * read), this re-creates the doc as the authenticated user using the
 * invitation referenced by `token`.
 *
 * Returns the freshly-created StaffUser on success, or null if the
 * invitation doesn't match or the writes don't go through.
 */
export const claimPendingInvitation = async (
  token: string,
  uid: string,
  authEmail: string | null,
): Promise<StaffUser | null> => {
  try {
    const invite = await getInvitation(token);
    if (!invite) return null;
    if (invite.status !== 'pending') return null;
    if (!authEmail) return null;
    if (invite.email.toLowerCase() !== authEmail.toLowerCase()) return null;

    const userDoc: Record<string, unknown> = {
      email: invite.email,
      role: invite.role,
      createdAt: Timestamp.now(),
      invitedBy: invite.createdBy,
      acceptedInviteId: invite.id,
    };
    await setDoc(doc(usersCol(), uid), userDoc);

    try {
      await updateDoc(doc(invitesCol(), invite.id), {
        status: 'accepted',
        acceptedAt: Timestamp.now(),
        acceptedBy: uid,
      });
    } catch (err) {
      console.warn('claimPendingInvitation: invitation status update failed', err);
    }

    return { id: uid, ...(userDoc as Omit<StaffUser, 'id'>) };
  } catch (err) {
    console.error('claimPendingInvitation failed', err);
    return null;
  }
};

/**
 * Compute the absolute URL a staff member should click to accept an invite.
 * Uses the current `window.location.origin`.
 */
export const buildInviteLink = (token: string): string => {
  const origin =
    typeof window !== 'undefined' && window.location?.origin
      ? window.location.origin
      : '';
  return `${origin}/invite/${token}`;
};
